import { REACTION } from '~/modules/reactions/types';

import { Api, Models, RequestParams } from './generated-api/api';

type GeneratedApi = Api<unknown>['api'];

type PlagiarismFileResponse =
  | ReturnType<GeneratedApi['v1CopyleaksExportsPdfDetail']>
  | ReturnType<GeneratedApi['v1ToolsPlagiarismExportsPdfDetail']>;

type ToolReactionResponse =
  | ReturnType<GeneratedApi['v1CopyleaksExportsReactionCreate']>
  | ReturnType<GeneratedApi['v1ZerogptAiDetectorExecutionsReactionCreate']>
  | ReturnType<GeneratedApi['v1AiHumanizerExecutionsReactionCreate']>
  | ReturnType<GeneratedApi['v1ToolsResultsReactionCreate']>
  | ReturnType<GeneratedApi['v1ToolsPlagiarismExportsReactionCreate']>
  | ReturnType<GeneratedApi['v1ToolsAiDetectorExecutionsReactionCreate']>
  | ReturnType<GeneratedApi['v1ToolsHumanizerExecutionsReactionCreate']>;

type AiHumanizerExecutionsResponse =
  | ReturnType<GeneratedApi['v1AiHumanizerExecutionsList']>
  | ReturnType<GeneratedApi['v1ToolsHumanizerExecutionsList']>;

type AiHumanizerUpdateResponse =
  | ReturnType<GeneratedApi['v1AiHumanizerExecutionsUpdate']>
  | ReturnType<GeneratedApi['v1ToolsResultsUpdate']>
  | ReturnType<GeneratedApi['v1ToolsHumanizerExecutionsUpdate']>;

type AiHumanizerDeleteResponse =
  | ReturnType<GeneratedApi['v1AiHumanizerExecutionsDelete']>
  | ReturnType<GeneratedApi['v1ToolsHumanizerExecutionsDelete']>;

type AiHumanizerToolResponse =
  | ReturnType<GeneratedApi['v1AiHumanizerList']>
  | ReturnType<GeneratedApi['v1ToolsHumanizerList']>;

type PlagiarismExecutionsResponse =
  | ReturnType<GeneratedApi['v1CopyleaksPlagiarismCheckExecutionsList']>
  | ReturnType<GeneratedApi['v1ToolsPlagiarismExecutionsList']>;

type PlagiarismDeleteResponse =
  | ReturnType<GeneratedApi['v1CopyleaksPlagiarismCheckExecutionsDelete']>
  | ReturnType<GeneratedApi['v1ToolsPlagiarismExecutionsDelete']>;

type PlagiarismToolResponse =
  | ReturnType<GeneratedApi['v1CopyleaksPlagiarismCheckList']>
  | ReturnType<GeneratedApi['v1ToolsPlagiarismList']>;

type PlagiarismReportEmailResponse =
  | ReturnType<GeneratedApi['v1CopyleaksExportsPdfEmailCreate']>
  | ReturnType<GeneratedApi['v1ToolsPlagiarismExportsPdfEmailCreate']>;

type AiDetectorExecutionsResponse =
  | ReturnType<GeneratedApi['v1ZerogptAiDetectorExecutionsList']>
  | ReturnType<GeneratedApi['v1ToolsAiDetectorExecutionsList']>;

type AiDetectorExecutionDetailResponse =
  | ReturnType<GeneratedApi['v1ZerogptAiDetectorExecutionsDetail']>
  | ReturnType<GeneratedApi['v1ToolsAiDetectorExecutionsDetail']>;

type AiDetectorDeleteResponse =
  | ReturnType<GeneratedApi['v1ZerogptAiDetectorExecutionsDelete']>
  | ReturnType<GeneratedApi['v1ToolsAiDetectorExecutionsDelete']>;

type AiDetectorReportEmailResponse =
  | ReturnType<GeneratedApi['v1ZerogptAiDetectorExecutionsSendPdfReportCreate']>
  | ReturnType<GeneratedApi['v1ToolsAiDetectorExecutionsSendPdfReportCreate']>;

type AiDetectorFactCheckResponse =
  | ReturnType<GeneratedApi['v1ZerogptAiDetectorExecutionsFactCheckDetail']>
  | ReturnType<GeneratedApi['v1ToolsAiDetectorExecutionsFactCheckDetail']>;

type AiDetectorToolResponse =
  | ReturnType<GeneratedApi['v1ZerogptAiDetectorList']>
  | ReturnType<GeneratedApi['v1ToolsAiDetectorList']>;

type MarkCopiedResponse =
  | ReturnType<GeneratedApi['v1CopyleaksExportsCopyCreate']>
  | ReturnType<GeneratedApi['v1AiHumanizerExecutionsCopyCreate']>
  | ReturnType<GeneratedApi['v1ToolsResultsMarkCopiedCreate']>
  | ReturnType<GeneratedApi['v1ToolsPlagiarismExportsCopyCreate']>
  | ReturnType<GeneratedApi['v1ToolsHumanizerExecutionsCopyCreate']>;

export type ToolHistoryQuery = {
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_type?: string;
};

export type ToolStreamEndpoint = {
  path: string;
  body: Record<string, unknown>;
};

export type PlagiarismCheckRequest = {
  text: string;
  simplified_scan: boolean;
};

export type HumanizerRequest = {
  original_text: string;
  mode: string;
  primary_execution_id?: string;
  primary_tool_id?: string;
};

export type AiDetectorRequest = {
  input: string;
  plagiarism_check_enabled?: boolean;
  primary_execution_id?: string;
  primary_tool_id?: string;
};

export type HumanizerUpdateRequest = {
  ai_response: string;
};

export type ToolApiClient = {
  api: {
    createHumanizerRequest: (
      data: HumanizerRequest,
      isFreemiumRequest?: boolean,
    ) => ToolStreamEndpoint;
    createAiDetectorRequest: (data: AiDetectorRequest) => ToolStreamEndpoint;
    getPlagiarismCheckerFile: (
      id: string,
      params: RequestParams,
    ) => PlagiarismFileResponse;
    sendCopyLeaksReaction: (
      id: string,
      reaction: REACTION,
      canonicalResultId: string | undefined,
      params: RequestParams,
    ) => ToolReactionResponse;
    sendZeroGPTReaction: (
      id: string,
      reaction: REACTION,
      canonicalResultId: string | undefined,
      params: RequestParams,
    ) => ToolReactionResponse;
    sendAiHumanizerReaction: (
      id: string,
      reaction: REACTION,
      canonicalResultId: string | undefined,
      params: RequestParams,
    ) => ToolReactionResponse;
    getAiHumanizerExecutions: (
      query: ToolHistoryQuery,
      params: RequestParams,
    ) => AiHumanizerExecutionsResponse;
    updateAiHumanizerExecution: (
      resultId: string,
      dataToUpdate: HumanizerUpdateRequest,
      canonicalResultId: string | undefined,
      params: RequestParams,
    ) => AiHumanizerUpdateResponse;
    deleteAiHumanizerExecutions: (
      params: RequestParams,
    ) => AiHumanizerDeleteResponse;
    getAiHumanizer: (params: RequestParams) => AiHumanizerToolResponse;
    getPlagiarismCheckerExecutions: (
      query: ToolHistoryQuery,
      params: RequestParams,
    ) => PlagiarismExecutionsResponse;
    deletePlagiarismCheckerExecutions: (
      params: RequestParams,
    ) => PlagiarismDeleteResponse;
    getPlagiarismChecker: (params: RequestParams) => PlagiarismToolResponse;
    sendPlagiarismReportEmail: (
      id: string,
      params: RequestParams,
    ) => PlagiarismReportEmailResponse;
    getAiDetectorExecutions: (
      query: ToolHistoryQuery,
      params: RequestParams,
    ) => AiDetectorExecutionsResponse;
    getAiDetectorExecutionByID: (
      id: string,
      canonicalExecutionId: string | undefined,
      params: RequestParams,
    ) => AiDetectorExecutionDetailResponse;
    deleteAiDetectorExecutions: (
      params: RequestParams,
    ) => AiDetectorDeleteResponse;
    sendAiDetectorReportEmail: (
      id: string,
      canonicalExecutionId: string | undefined,
      params: RequestParams,
    ) => AiDetectorReportEmailResponse;
    getAiDetectorFactChecking: (
      id: string,
      canonicalExecutionId: string | undefined,
      params: RequestParams,
    ) => AiDetectorFactCheckResponse;
    getAiDetector: (params: RequestParams) => AiDetectorToolResponse;
    markCopyleaksExecutionsResultsCopied: (
      executionId: string,
      canonicalResultId: string | undefined,
      params: RequestParams,
    ) => MarkCopiedResponse;
    markAiHumanizerExecutionsResultsCopied: (
      executionId: string,
      canonicalResultId: string | undefined,
      params: RequestParams,
    ) => MarkCopiedResponse;
  };
};

const TOOL_ROUTE_IDS = {
  plagiarism: 'plagiarism',
  humanizer: 'humanizer',
  aiDetector: 'ai-detector',
} as const;

const TOOL_REACTION_BY_REACTION: Record<REACTION, Models> = {
  [REACTION.LIKE]: Models.ReactionLike,
  [REACTION.DISLIKE]: Models.ReactionDislike,
  [REACTION.NONE]: Models.ReactionNeutral,
};

export const createLegacyToolApiClient = (
  api: GeneratedApi,
): ToolApiClient => ({
  api: {
    createHumanizerRequest: (data, isFreemiumRequest = false) => ({
      path: `/api/v2/ai-humanizer${isFreemiumRequest ? '?is_freemium_request=true' : ''}`,
      body: data,
    }),
    createAiDetectorRequest: (data) => ({
      path: '/api/v1/zerogpt/ai-detector',
      body: data,
    }),
    getPlagiarismCheckerFile: (id, params) =>
      api.v1CopyleaksExportsPdfDetail(id, params),
    sendCopyLeaksReaction: (id, reaction, _canonicalResultId, params) =>
      api.v1CopyleaksExportsReactionCreate(id, { reaction }, params),
    sendZeroGPTReaction: (id, reaction, _canonicalResultId, params) =>
      api.v1ZerogptAiDetectorExecutionsReactionCreate(
        id,
        { reaction: TOOL_REACTION_BY_REACTION[reaction] },
        params,
      ),
    sendAiHumanizerReaction: (id, reaction, _canonicalResultId, params) =>
      api.v1AiHumanizerExecutionsReactionCreate(id, { reaction }, params),
    getAiHumanizerExecutions: (query, params) =>
      api.v1AiHumanizerExecutionsList(query, params),
    updateAiHumanizerExecution: (
      resultId,
      dataToUpdate,
      _canonicalResultId,
      params,
    ) => api.v1AiHumanizerExecutionsUpdate(resultId, dataToUpdate, params),
    deleteAiHumanizerExecutions: (params) =>
      api.v1AiHumanizerExecutionsDelete(params),
    getAiHumanizer: (params) => api.v1AiHumanizerList(params),
    getPlagiarismCheckerExecutions: (query, params) =>
      api.v1CopyleaksPlagiarismCheckExecutionsList(query, params),
    deletePlagiarismCheckerExecutions: (params) =>
      api.v1CopyleaksPlagiarismCheckExecutionsDelete(params),
    getPlagiarismChecker: (params) =>
      api.v1CopyleaksPlagiarismCheckList(params),
    sendPlagiarismReportEmail: (id, params) =>
      api.v1CopyleaksExportsPdfEmailCreate(id, params),
    getAiDetectorExecutions: (query, params) =>
      api.v1ZerogptAiDetectorExecutionsList(query, params),
    getAiDetectorExecutionByID: (id, _canonicalExecutionId, params) =>
      api.v1ZerogptAiDetectorExecutionsDetail(id, params),
    deleteAiDetectorExecutions: (params) =>
      api.v1ZerogptAiDetectorExecutionsDelete(params),
    sendAiDetectorReportEmail: (id, _canonicalExecutionId, params) =>
      api.v1ZerogptAiDetectorExecutionsSendPdfReportCreate(id, params),
    getAiDetectorFactChecking: (id, _canonicalExecutionId, params) =>
      api.v1ZerogptAiDetectorExecutionsFactCheckDetail(id, params),
    getAiDetector: (params) => api.v1ZerogptAiDetectorList(params),
    markCopyleaksExecutionsResultsCopied: (
      executionId,
      _canonicalResultId,
      params,
    ) => api.v1CopyleaksExportsCopyCreate(executionId, params),
    markAiHumanizerExecutionsResultsCopied: (
      executionId,
      _canonicalResultId,
      params,
    ) => api.v1AiHumanizerExecutionsCopyCreate(executionId, params),
  },
});

export const createPolicyToolApiClient = (
  api: GeneratedApi,
): ToolApiClient => ({
  api: {
    createHumanizerRequest: (data) => ({
      path: '/api/v1/tools/humanizer/humanize',
      body: data,
    }),
    createAiDetectorRequest: (data) => ({
      path: '/api/v1/tools/ai-detector/detect',
      body: {
        ...data,
        input_text: data.input,
      },
    }),
    getPlagiarismCheckerFile: (id, params) =>
      api.v1ToolsPlagiarismExportsPdfDetail(id, params),
    sendCopyLeaksReaction: (id, reaction, canonicalResultId, params) =>
      canonicalResultId
        ? api.v1ToolsResultsReactionCreate(
            TOOL_ROUTE_IDS.plagiarism,
            canonicalResultId,
            { reaction: TOOL_REACTION_BY_REACTION[reaction] },
            params,
          )
        : api.v1ToolsPlagiarismExportsReactionCreate(id, { reaction }, params),
    sendZeroGPTReaction: (id, reaction, canonicalResultId, params) =>
      canonicalResultId
        ? api.v1ToolsResultsReactionCreate(
            TOOL_ROUTE_IDS.aiDetector,
            canonicalResultId,
            { reaction: TOOL_REACTION_BY_REACTION[reaction] },
            params,
          )
        : api.v1ToolsAiDetectorExecutionsReactionCreate(
            id,
            { reaction: TOOL_REACTION_BY_REACTION[reaction] },
            params,
          ),
    sendAiHumanizerReaction: (id, reaction, canonicalResultId, params) =>
      canonicalResultId
        ? api.v1ToolsResultsReactionCreate(
            TOOL_ROUTE_IDS.humanizer,
            canonicalResultId,
            { reaction: TOOL_REACTION_BY_REACTION[reaction] },
            params,
          )
        : api.v1ToolsHumanizerExecutionsReactionCreate(
            id,
            { reaction },
            params,
          ),
    getAiHumanizerExecutions: (query, params) =>
      api.v1ToolsHumanizerExecutionsList(query, params),
    updateAiHumanizerExecution: (
      resultId,
      dataToUpdate,
      canonicalResultId,
      params,
    ) =>
      canonicalResultId
        ? api.v1ToolsResultsUpdate(
            canonicalResultId,
            TOOL_ROUTE_IDS.humanizer,
            dataToUpdate,
            params,
          )
        : api.v1ToolsHumanizerExecutionsUpdate(resultId, dataToUpdate, params),
    deleteAiHumanizerExecutions: (params) =>
      api.v1ToolsHumanizerExecutionsDelete(params),
    getAiHumanizer: (params) => api.v1ToolsHumanizerList(params),
    getPlagiarismCheckerExecutions: (query, params) =>
      api.v1ToolsPlagiarismExecutionsList(query, params),
    deletePlagiarismCheckerExecutions: (params) =>
      api.v1ToolsPlagiarismExecutionsDelete(params),
    getPlagiarismChecker: (params) => api.v1ToolsPlagiarismList(params),
    sendPlagiarismReportEmail: (id, params) =>
      api.v1ToolsPlagiarismExportsPdfEmailCreate(id, params),
    getAiDetectorExecutions: (query, params) =>
      api.v1ToolsAiDetectorExecutionsList(query, params),
    getAiDetectorExecutionByID: (id, canonicalExecutionId, params) =>
      api.v1ToolsAiDetectorExecutionsDetail(canonicalExecutionId || id, params),
    deleteAiDetectorExecutions: (params) =>
      api.v1ToolsAiDetectorExecutionsDelete(params),
    sendAiDetectorReportEmail: (id, canonicalExecutionId, params) =>
      api.v1ToolsAiDetectorExecutionsSendPdfReportCreate(
        canonicalExecutionId || id,
        params,
      ),
    getAiDetectorFactChecking: (id, canonicalExecutionId, params) =>
      api.v1ToolsAiDetectorExecutionsFactCheckDetail(
        canonicalExecutionId || id,
        params,
      ),
    getAiDetector: (params) => api.v1ToolsAiDetectorList(params),
    markCopyleaksExecutionsResultsCopied: (
      executionId,
      canonicalResultId,
      params,
    ) =>
      canonicalResultId
        ? api.v1ToolsResultsMarkCopiedCreate(
            TOOL_ROUTE_IDS.plagiarism,
            canonicalResultId,
            params,
          )
        : api.v1ToolsPlagiarismExportsCopyCreate(executionId, params),
    markAiHumanizerExecutionsResultsCopied: (
      executionId,
      canonicalResultId,
      params,
    ) =>
      canonicalResultId
        ? api.v1ToolsResultsMarkCopiedCreate(
            TOOL_ROUTE_IDS.humanizer,
            canonicalResultId,
            params,
          )
        : api.v1ToolsHumanizerExecutionsCopyCreate(executionId, params),
  },
});
